import { tr, withLang, getCurrentLang } from "../i18n/LanguageContext";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate, localizeTo } from "../i18n/router";
import { Mail, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const { requestMagicLink, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get the redirect parameter if it exists
  const redirectTo = searchParams.get('redirect');
  
  // If user is already logged in, redirect them
  useEffect(() => {
    if (user) {
      console.log('[LoginPage] User already logged in, redirecting to homepage');
      navigate('/');
    }
  }, [user, navigate]);
  
  // Store redirect path in localStorage for use after magic link callback
  // Only store non-root redirects, and clear any stale redirects
  useEffect(() => {
    console.log('[LoginPage] redirectTo param:', redirectTo);
    if (redirectTo && redirectTo !== '/' && redirectTo.startsWith('/')) {
      localStorage.setItem('loginRedirect', localizeTo(redirectTo));
      console.log('[LoginPage] Stored loginRedirect:', redirectTo);
    } else if (getCurrentLang() !== 'ru') {
      localStorage.setItem('loginRedirect', localizeTo('/'));
    } else {
      // Clear any stale redirect when user visits /login directly
      const existingRedirect = localStorage.getItem('loginRedirect');
      if (existingRedirect) {
        console.log('[LoginPage] Clearing stale loginRedirect:', existingRedirect);
        localStorage.removeItem('loginRedirect');
      }
    }
  }, [redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage(tr("Пожалуйста, введите email адрес"));
      setIsSuccess(false);
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const result = await requestMagicLink(email);
      
      if (result.success) {
        setMessage(tr(result.message));
        setIsSuccess(true);
        setEmail(""); // Clear email field
      } else {
        setMessage(tr(result.message));
        setIsSuccess(false);
      }
    } catch (error) {
      setMessage(tr("Произошла неожиданная ошибка"));
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-8 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="eunoia-title-large mb-6">
            {tr("Войти в аккаунт")}
          </h1>
          <p className="eunoia-body-large">
            {tr("Мы отправим вам ссылку для безопасного входа")}
          </p>
        </div>

        <div className="eunoia-surface p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Mail className="h-6 w-6" style={{color: 'var(--eunoia-accent)'}} />
              <h2 className="eunoia-title-small">{tr("Вход без пароля")}</h2>
            </div>
            <p className="eunoia-body">
              {tr("Введите ваш email адрес, и мы отправим вам ссылку для входа")}
            </p>
          </div>
          
          
          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block eunoia-caption mb-3">
                  {tr("Email адрес")}
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="eunoia-input"
                  disabled={loading}
                  required
                />
              </div>

              {message && !isSuccess && (
                <div className="eunoia-surface-soft p-4" style={{borderLeft: '3px solid #dc2626'}}>
                  <p className="eunoia-caption text-red-700">{message}</p>
                </div>
              )}

              <button
                type="submit"
                className="eunoia-btn-primary w-full flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {tr("Отправить ссылку для входа")}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto" 
                   style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}>
                <CheckCircle className="h-10 w-10" style={{color: 'var(--eunoia-accent)'}} />
              </div>
              
              <div className="space-y-4">
                <h3 className="eunoia-title-small">
                  {tr("Ссылка отправлена!")}
                </h3>
                <p className="eunoia-body">
                  {message}
                </p>
                <p className="eunoia-caption">
                  {tr("Проверьте папку \"Спам\", если письмо не пришло в течение нескольких минут.")}
                </p>
              </div>

              <div className="pt-4">
                <button
                  onClick={() => {
                    setIsSuccess(false);
                    setMessage(null);
                  }}
                  className="eunoia-btn-secondary w-full"
                >
                  {tr("Отправить другой email")}
                </button>
              </div>
            </div>
            )}

          <div className="mt-8 text-center">
            <p className="eunoia-caption">
              {tr("Нажимая «Отправить ссылку для входа», вы соглашаетесь с нашими")}{" "}
              <Link to={withLang("/terms", getCurrentLang())} className="hover:underline" style={{color: 'var(--eunoia-accent)'}} data-testid="login-terms-link">
                {tr("условиями использования")}
              </Link>{" "}
              {tr("и")}{" "}
              <Link to={withLang("/privacy", getCurrentLang())} className="hover:underline" style={{color: 'var(--eunoia-accent)'}} data-testid="login-privacy-link">
                {tr("политикой конфиденциальности")}
              </Link>
            </p>
          </div>
        </div>

        <div className="text-center mt-8">
          <Link 
            to={withLang("/", getCurrentLang())}
            data-testid="login-back-home-link"
            className="eunoia-caption hover:underline"
            style={{color: 'var(--eunoia-accent)'}}
          >
            {tr("← Вернуться на главную")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;